/**
 * JSON-RPC 2.0 WebSocket client for the HelpPilot agent dashboard.
 *
 * Connects to /api/v1/ws/agent?companyId={id}
 * Subprotocol: helppilot.realtime.v1
 * Reconnects with exponential backoff (1s -> 30s max, unlimited attempts)
 * 30s ping interval
 */

const WS_SUBPROTOCOL = "helppilot.realtime.v1";
const PING_INTERVAL_MS = 30_000;
const ACK_TIMEOUT_MS = 5_000;
const BASE_RECONNECT_DELAY_MS = 1_000;
const MAX_RECONNECT_DELAY_MS = 30_000;

type EventHandler = (params: Record<string, unknown>) => void;

interface PendingRequest {
  resolve: (result: unknown) => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function getWSBaseUrl(): string {
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${window.location.host}/api/v1`;
}

class WSClient {
  private socket: WebSocket | null = null;
  private companyId: string | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  private intentionalClose = false;
  private pendingRequests = new Map<string, PendingRequest>();
  private eventHandlers = new Map<string, Set<EventHandler>>();

  /** Callback invoked when connection state changes */
  onConnectionChange: ((connected: boolean) => void) | null = null;
  /** Callback invoked when reconnection state changes */
  onReconnectingChange: ((reconnecting: boolean) => void) | null = null;

  /**
   * Manually trigger a reconnect (e.g. user clicks the disconnected icon).
   * Resets the backoff counter and attempts immediately.
   */
  manualReconnect(): void {
    if (!this.companyId) return;
    if (this.socket?.readyState === WebSocket.OPEN) return;

    this.cleanup();
    if (this.socket) {
      this.socket.onclose = null;
      this.socket.onerror = null;
      this.socket.close();
      this.socket = null;
    }

    this.intentionalClose = false;
    this.reconnectAttempts = 0;
    this.onReconnectingChange?.(true);
    this.createConnection();
  }

  connect(companyId: string): void {
    // If already connected to the same company, skip
    if (this.socket && this.companyId === companyId && this.socket.readyState === WebSocket.OPEN) {
      return;
    }

    // If connected to a different company, disconnect first
    if (this.socket) {
      this.disconnect();
    }

    this.companyId = companyId;
    this.intentionalClose = false;
    this.reconnectAttempts = 0;
    this.createConnection();
  }

  disconnect(): void {
    this.intentionalClose = true;
    this.cleanup();

    if (this.socket) {
      this.socket.close(1000, "Client disconnect");
      this.socket = null;
    }

    this.companyId = null;
    this.onConnectionChange?.(false);
    this.onReconnectingChange?.(false);
  }

  /**
   * Send a JSON-RPC request and wait for a response.
   * Returns a promise that resolves with the result or rejects on error/timeout.
   */
  send(method: string, params: Record<string, unknown> = {}): Promise<unknown> {
    return new Promise((resolve, reject) => {
      if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
        reject(new Error("WebSocket is not connected"));
        return;
      }

      const id = generateId();

      const timer = setTimeout(() => {
        this.pendingRequests.delete(id);
        reject(new Error(`Request ${method} timed out after ${ACK_TIMEOUT_MS}ms`));
      }, ACK_TIMEOUT_MS);

      this.pendingRequests.set(id, { resolve, reject, timer });

      const message = JSON.stringify({
        jsonrpc: "2.0",
        method,
        params,
        id,
      });

      this.socket.send(message);
    });
  }

  /**
   * Send a JSON-RPC notification (no response expected).
   */
  notify(method: string, params: Record<string, unknown> = {}): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return;
    }

    const message = JSON.stringify({
      jsonrpc: "2.0",
      method,
      params,
    });

    this.socket.send(message);
  }

  /**
   * Subscribe to a server push event.
   */
  on(event: string, handler: EventHandler): void {
    let handlers = this.eventHandlers.get(event);
    if (!handlers) {
      handlers = new Set();
      this.eventHandlers.set(event, handlers);
    }
    handlers.add(handler);
  }

  /**
   * Unsubscribe from a server push event.
   */
  off(event: string, handler: EventHandler): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.delete(handler);
      if (handlers.size === 0) {
        this.eventHandlers.delete(event);
      }
    }
  }

  private createConnection(): void {
    if (!this.companyId) return;

    const url = `${getWSBaseUrl()}/ws/agent?companyId=${encodeURIComponent(this.companyId)}`;

    try {
      this.socket = new WebSocket(url, WS_SUBPROTOCOL);
    } catch {
      this.scheduleReconnect();
      return;
    }

    this.socket.onopen = () => {
      this.reconnectAttempts = 0;
      this.onConnectionChange?.(true);
      this.onReconnectingChange?.(false);
      this.startPing();
    };

    this.socket.onmessage = (event: MessageEvent) => {
      this.handleMessage(event.data as string);
    };

    this.socket.onclose = (event: CloseEvent) => {
      this.cleanup();
      this.onConnectionChange?.(false);

      if (!this.intentionalClose && event.code !== 1000) {
        this.scheduleReconnect();
      }
    };

    this.socket.onerror = () => {
      // The onclose handler will fire after onerror, which handles reconnection
    };
  }

  private handleMessage(raw: string): void {
    let data: Record<string, unknown>;
    try {
      data = JSON.parse(raw) as Record<string, unknown>;
    } catch {
      return;
    }

    // Server response to a client request (has id + result or error)
    if (typeof data["id"] === "string" && ("result" in data || "error" in data)) {
      const pending = this.pendingRequests.get(data["id"]);
      if (pending) {
        clearTimeout(pending.timer);
        this.pendingRequests.delete(data["id"]);

        if ("error" in data) {
          const err = data["error"] as { code: number; message: string };
          pending.reject(new Error(`WS Error ${err.code}: ${err.message}`));
        } else {
          pending.resolve(data["result"]);
        }
      }
      return;
    }

    // Server push event (has method + params, no id to match a pending request)
    if (typeof data["method"] === "string" && typeof data["params"] === "object") {
      const handlers = this.eventHandlers.get(data["method"]);
      if (handlers) {
        const params = data["params"] as Record<string, unknown>;
        for (const handler of handlers) {
          try {
            handler(params);
          } catch {
            // Swallow handler errors to avoid breaking the event loop
          }
        }
      }
    }
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = setInterval(() => {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        // Send a JSON-RPC ping notification
        this.notify("ping");
      }
    }, PING_INTERVAL_MS);
  }

  private stopPing(): void {
    if (this.pingTimer !== null) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private scheduleReconnect(): void {
    this.onReconnectingChange?.(true);

    const delay = Math.min(
      BASE_RECONNECT_DELAY_MS * Math.pow(2, this.reconnectAttempts),
      MAX_RECONNECT_DELAY_MS,
    );

    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempts++;
      this.createConnection();
    }, delay);
  }

  private cleanup(): void {
    this.stopPing();

    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    // Reject all pending requests
    for (const [id, pending] of this.pendingRequests) {
      clearTimeout(pending.timer);
      pending.reject(new Error("WebSocket disconnected"));
      this.pendingRequests.delete(id);
    }
  }
}

let instance: WSClient | null = null;

/**
 * Get the singleton WSClient instance.
 */
export function getWSClient(): WSClient {
  if (!instance) {
    instance = new WSClient();
  }
  return instance;
}
